/**
 * HRDailyReportPage
 * HR self-service page:
 *  – Checks today's report status on load
 *  – Shows ReportForm (locked if already submitted today)
 *  – Lists own past reports (paginated)
 */
import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import ReportForm from '../components/DailyReport/molecules/ReportForm';
import ReportList from '../components/DailyReport/organisms/ReportList';
import dailyReportService from '../services/dailyReportService';
import {
  MdAssignment,
  MdHistory,
  MdCheckCircle,
  MdPendingActions
} from 'react-icons/md';

const HRDailyReportPage = () => {
  const { user } = useAuth();

  // Today's report
  const [todayReport, setTodayReport]   = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);

  // History
  const [reports, setReports]         = useState([]);
  const [pagination, setPagination]   = useState({ page: 1, totalPages: 1, total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Load today's report ───────────────────────────────────────────────────
  const fetchToday = useCallback(async () => {
    try {
      setTodayLoading(true);
      const res = await dailyReportService.getTodayReport();
      if (res.success) setTodayReport(res.data.report);
    } catch {
      /* silent – form will just be editable */
    } finally {
      setTodayLoading(false);
    }
  }, []);

  // ── Load past reports ──────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setHistoryLoading(true);
      const res = await dailyReportService.getMyReports({ page, limit: 10 });
      if (res.success) {
        setReports(res.data.items);
        setPagination(res.data.pagination);
      }
    } catch {
      /* silent */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);
  useEffect(() => { fetchHistory(currentPage); }, [fetchHistory, currentPage]);

  // ── After form submit → refresh both ──────────────────────────────────────
  const handleReportSuccess = (report) => {
    setTodayReport(report);
    fetchHistory(1);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // ── Stats cards ───────────────────────────────────────────────────────────
  const reviewed = reports.filter((r) => r.status === 'reviewed').length;
  const pending  = reports.filter((r) => r.status === 'pending').length;

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* ── Page Header ── */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-green/10 flex items-center justify-center flex-shrink-0">
            <MdAssignment size={22} className="text-accent-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight">Daily Report</h1>
            <p className="text-sm text-text-secondary">
              Hi {user?.name || 'there'}, submit your end-of-day report before punching out.
            </p>
          </div>
        </div>

        {/* ── Quick Stats ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={MdAssignment}
            value={pagination.total}
            label="Total Reports"
            color="text-accent-green"
            bg="bg-accent-green/10"
          />
        </div>

        {/* ── Today's Report Section ── */}
        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-6">
          <h2 className="text-base font-semibold text-text-primary mb-5 flex items-center gap-2">
            <MdAssignment size={18} className="text-accent-green" />
            Today's Report
            <span className="text-xs font-normal text-text-tertiary ml-1">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </span>
          </h2>

          {todayLoading ? (
            <div className="flex items-center gap-2 py-4 text-text-tertiary text-sm">
              <div className="w-4 h-4 border-2 border-border border-t-accent-green rounded-full animate-spin" />
              Checking report status…
            </div>
          ) : (
            <ReportForm
              existingReport={todayReport}
              onSuccess={handleReportSuccess}
            />
          )}
        </div>

        {/* ── History Section ── */}
        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-6">
          <h2 className="text-base font-semibold text-text-primary mb-5 flex items-center gap-2">
            <MdHistory size={18} className="text-accent-green" />
            Report History
          </h2>

          <ReportList
            reports={reports}
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={historyLoading}
            isChairman={false}
          />
        </div>

      </div>
    </MainLayout>
  );
};

const StatCard = ({ icon: Icon, value, label, color, bg }) => (
  <div className={`rounded-xl p-4 ${bg} border border-transparent flex flex-col gap-1`}>
    <Icon size={20} className={color} />
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs font-medium text-text-secondary">{label}</p>
  </div>
);

export default HRDailyReportPage;
